import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, PlayCircle, Check, Sparkles, ShieldCheck, Users, MessageSquareQuote, PackagePlus, TruckIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { AlertSettingsWidget } from "@/components/dashboard/AlertSettingsWidget";
import {
  useShipperGettingStartedProgress,
  type GettingStartedProgress,
} from "@/hooks/useShipperGettingStartedProgress";

type StepKey = keyof GettingStartedProgress;

type Step = {
  id: StepKey;
  title: string;
  description: string;
  route: string;
  videoUrl: string;
  icon: React.ComponentType<{ className?: string }>;
};

const STEPS: Step[] = [
  {
    id: "network",
    title: "Build Your Network",
    description:
      "Add the brokers you already trust to your private network so you can send them loads directly and get competitive bids.",
    route: "/lr-direct/network-settings",
    videoUrl: "https://youtu.be/pndY6Ns1zR0",
    icon: Users,
  },
  {
    id: "creditProfile",
    title: "Credit Profile",
    description:
      "Fill in your credit info so our AI can instantly complete your credit app when you meet new brokers on the open market — move your loads fast.",
    route: "/company-settings?tab=credit-profile",
    videoUrl: "https://youtu.be/JWX34ec3Bxg",
    icon: ShieldCheck,
  },
  {
    id: "quote",
    title: "Get a Quote",
    description: "Request a rate quote from your brokers before you commit.",
    route: "/lr-direct/post/start?mode=quote",
    videoUrl: "https://youtu.be/SgUoTcG3acU",
    icon: MessageSquareQuote,
  },
  {
    id: "load",
    title: "Post a Load",
    description:
      "Post your freight to your private network or the open market for brokers to bid.",
    route: "/lr-direct/post/start",
    videoUrl: "https://youtu.be/uAaroXXTXK4",
    icon: PackagePlus,
  },
  {
    id: "dispatch",
    title: "Build a Dispatch",
    description: "Turn your awarded load into a dispatch and move it.",
    route: "/lr-direct/my-loads?stage=dispatching",
    videoUrl: "https://youtu.be/iSOAlKOUHuM",
    icon: TruckIcon,
  },
];

export function ShipperGettingStarted() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { progress, loaded } = useShipperGettingStartedProgress(user?.id);

  const [collapsed, setCollapsed] = useState(false);
  const userToggledRef = useRef(false);
  const autoCollapsedRef = useRef(false);

  const completeCount = STEPS.filter((s) => progress[s.id]).length;
  const allComplete = loaded && completeCount === STEPS.length;

  // Auto-collapse once when everything is complete; respect any subsequent
  // manual toggle by the user.
  useEffect(() => {
    if (allComplete && !autoCollapsedRef.current && !userToggledRef.current) {
      autoCollapsedRef.current = true;
      setCollapsed(true);
    }
  }, [allComplete]);

  const handleToggle = () => {
    userToggledRef.current = true;
    setCollapsed((c) => !c);
  };

  const incomplete = loaded && !allComplete;

  return (
    <Card
      className={cn(
        "overflow-hidden transition-colors",
        incomplete && "bg-amber-200/70 dark:bg-amber-900/40 border-amber-400 dark:border-amber-700"
      )}
    >
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          "w-full flex items-center gap-3 p-4 text-left transition-colors",
          incomplete ? "hover:bg-amber-300/40 dark:hover:bg-amber-900/60" : "hover:bg-muted/40"
        )}
        aria-expanded={!collapsed}
      >
        <div className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg shrink-0",
          incomplete ? "bg-amber-500/20" : "bg-brand-teal/10"
        )}>
          <Sparkles className={cn("h-4 w-4", incomplete ? "text-amber-700 dark:text-amber-300" : "text-brand-teal")} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn(
            "text-[10px] font-bold uppercase tracking-widest",
            incomplete ? "text-amber-800 dark:text-amber-200" : "text-brand-teal-deep"
          )}>
            {incomplete ? "Setup Incomplete — Start Here" : "Getting Started"}
          </div>
          <div className="text-sm font-semibold text-foreground">
            {completeCount} of {STEPS.length} steps complete
          </div>
        </div>
        {collapsed ? (
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        ) : (
          <ChevronUp className="h-5 w-5 text-muted-foreground" />
        )}
      </button>

      {!collapsed && (
        <div className="px-4 pb-4">
          <p className={cn(
            "text-xs mb-1 font-semibold",
            incomplete ? "text-amber-900 dark:text-amber-100" : "text-muted-foreground"
          )}>
            Start with Step 1 below to set up your account in 5 quick steps.
          </p>
          <p className={cn(
            "text-xs mb-3",
            incomplete ? "text-amber-800/80 dark:text-amber-200/80" : "text-muted-foreground"
          )}>
            New here? Watch the short video on each step.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {STEPS.map((step, idx) => {
              const Icon = step.icon;
              const complete = progress[step.id];
              return (
                <div
                  key={step.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(step.route)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      navigate(step.route);
                    }
                  }}
                  className={cn(
                    "group relative flex flex-col rounded-lg border bg-card p-3 text-left transition-all cursor-pointer",
                    "hover:border-brand-teal hover:shadow-md",
                    complete
                      ? "border-brand-teal/40 bg-brand-teal/5"
                      : incomplete &&
                          "bg-amber-100 dark:bg-amber-800/40 border-amber-400 dark:border-amber-600 hover:border-amber-500"
                  )}
                >
                  <div
                    className={cn(
                      "absolute top-2 right-2 flex h-5 w-5 items-center justify-center rounded-full border",
                      complete
                        ? "bg-brand-teal border-brand-teal text-white"
                        : "bg-background border-muted-foreground/30 text-transparent"
                    )}
                    aria-label={complete ? "Complete" : "Not complete"}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <div className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-md shrink-0",
                      complete || !incomplete
                        ? "bg-brand-teal/10 text-brand-teal"
                        : "bg-amber-500/20 text-amber-800 dark:text-amber-200"
                    )}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className={cn(
                      "text-[10px] font-bold uppercase tracking-wider",
                      complete || !incomplete ? "text-muted-foreground" : "text-amber-800 dark:text-amber-200"
                    )}>
                      Step {idx + 1}
                    </span>
                  </div>

                  <h3 className="text-sm font-bold text-foreground mb-1 pr-6">
                    {step.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-snug mb-3 flex-1">
                    {step.description}
                  </p>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 self-start text-xs text-brand-teal hover:text-brand-teal hover:bg-brand-teal/10 gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(step.videoUrl, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <PlayCircle className="h-3.5 w-3.5" />
                    Watch Video
              </Button>
                </div>
              );
            })}
          </div>

          <div className="mt-4 pt-4 border-t border-border/60">
            <AlertSettingsWidget />
          </div>
        </div>
      )}
    </Card>
  );
}

export default ShipperGettingStarted;
